import dayjs from "dayjs";
import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Grid, Typography, CircularProgress, Chip } from "@material-ui/core";
import { Assignment, Equalizer, TurnedIn, DateRange } from "@material-ui/icons";

import { apiCall } from "../../../../utils/api";
import { API_URLS } from "../../../../constants/api";
import { KPTextfield } from "../../../../components/KPTComponents";
import { notification } from "reducer/home/action";
import select from "../../../../utils/select";
import SectionCard from "../../utils/SectionCard";

// SettingForm — Cấu hình hệ thống cho Đề tài Cơ sở.
// Refactor 2026-05-16: rebuild giống SR pattern (SectionCard + status chip +
// warning thứ tự + validate đầy đủ). Khác SR ở 3 mốc chính (không có conference):
//   - Đăng ký hồ sơ           → regStartTime / regEndTime
//   - Báo cáo tiến độ (2y)    → report1StartTime / report1EndTime
//   - Nghiệm thu              → acceptStartTime / acceptEndTime
//
// BE schema YearlyDueDate dùng chung cho cả 2 module (type=internal vs student).
// BCTĐ chỉ 1 lần ở đề tài Cơ sở 2y (khác SR có 2 BCTĐ). Conference window không
// dùng vì không có "Hội nghị NCKH cấp trường" cho đề tài Cơ sở.
function SettingForm(props) {
  const dispatch = useDispatch();
  const profileDetail = useSelector((s) => select(s, "profileReducer", "detail"));
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(false);

  const sections = [
    { icon: <Assignment />, title: "Đăng ký hồ sơ",       startKey: "regStart",     endKey: "regEnd",     beStart: "regStartTime",     beEnd: "regEndTime" },
    { icon: <Equalizer />,  title: "Báo cáo tiến độ (2y)", startKey: "report1Start", endKey: "report1End", beStart: "report1StartTime", beEnd: "report1EndTime" },
    { icon: <TurnedIn />,   title: "Nghiệm thu đề tài",   startKey: "acceptStart",  endKey: "acceptEnd",  beStart: "acceptStartTime",  beEnd: "acceptEndTime" },
  ];

  // BE column YearlyDueDate giờ là DATE (không có time + tz). ISO timestamp
  // "2026-05-15T00:00:00Z" → lấy 10 ký tự đầu để tránh shift ngày theo browser TZ.
  const formatBEDate = (iso) => {
    if (!iso) return "";
    const datePart = String(iso).substring(0, 10);
    const [y, m, d] = datePart.split("-");
    if (!y || !m || !d) return "";
    return `${d}/${m}/${y}`;
  };

  const parseDDMMYYYY = (s) => {
    if (!s || typeof s !== "string") return null;
    const [d, m, y] = s.split("/");
    if (!d || !m || !y) return null;
    const dj = dayjs(`${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`);
    return dj.isValid() ? dj : null;
  };

  const dateOnlyISO = (s) => {
    if (!s) return null;
    const [d, m, y] = s.split("/");
    if (!d || !m || !y) return null;
    return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}T00:00:00Z`;
  };

  const getData = useCallback(async () => {
    setLoading(true);
    try {
      const api = API_URLS.YEARLY_DUE_DATE.filterYearlyDueDate();
      const { response, error } = await apiCall({
        ...api,
        payload: { type: "internal", schoolYear: props.year },
      });
      if (!error && response.status === 200 && response.data.success) {
        const res = response.data.data[0] || {};
        const next = { ...res };
        sections.forEach((s) => {
          next[s.startKey] = formatBEDate(res?.[s.beStart]);
          next[s.endKey] = formatBEDate(res?.[s.beEnd]);
        });
        setValues(next);
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.year]);

  useEffect(() => {
    getData();
  }, [getData]);

  const handleChange = (key, val) => setValues((v) => ({ ...v, [key]: val }));

  const sectionStatus = (s) => {
    const start = parseDDMMYYYY(values?.[s.startKey]);
    const end = parseDDMMYYYY(values?.[s.endKey]);
    if (!start || !end) return { label: "Chưa cấu hình", bg: "#bdbdbd" };
    const now = dayjs();
    if (now.isBefore(start, "day")) {
      const days = start.diff(now, "day");
      return { label: `Sắp mở (còn ${days} ngày)`, bg: "#ff9800" };
    }
    if (now.isAfter(end, "day")) return { label: "Đã đóng", bg: "#9e9e9e" };
    return { label: "Đang mở", bg: "#4caf50" };
  };

  // 2026-08-11: bỏ ràng buộc thứ tự GIỮA các mốc (vd "báo cáo tiến độ phải
  // sau ngày đăng ký") — vẫn giữ check end >= start TRONG cùng 1 mốc theo
  // yêu cầu.
  const validate = () => {
    for (const s of sections) {
      const sv = values?.[s.startKey];
      const ev = values?.[s.endKey];
      if (!sv && !ev) continue;
      if (!sv || !ev) return `Mốc "${s.title}" thiếu ngày bắt đầu hoặc kết thúc.`;
      const sd = parseDDMMYYYY(sv);
      const ed = parseDDMMYYYY(ev);
      if (!sd || !ed) return `Ngày không hợp lệ ở mốc "${s.title}".`;
      if (ed.isBefore(sd, "day")) return `Ngày kết thúc phải ≥ ngày bắt đầu ở mốc "${s.title}".`;
    }
    return null;
  };

  const updateData = useCallback(
    async (payload) => {
      setLoading(true);
      const api = API_URLS.YEARLY_DUE_DATE.updateYearlyDueDate();
      try {
        const { response, error } = await apiCall({ ...api, payload });
        if (!error && response.status === 200 && response.data.success) {
          dispatch(notification({ type: "success", message: "Cập nhật thời gian thành công!" }));
          props.handleClose();
        } else {
          const msg =
            error?.response?.data?.message ||
            response?.data?.message ||
            "Cập nhật thời gian thất bại!";
          dispatch(notification({ type: "error", message: msg }));
        }
      } finally {
        setLoading(false);
      }
    },
    [dispatch, props]
  );

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    const reason = validate();
    if (reason) {
      dispatch(notification({ type: "warning", message: reason }));
      return;
    }
    const payload = {
      ...values,
      year: props.year,
      schoolYear: props.year,
      type: "internal",
      updatedById: profileDetail?.id || 0,
      updatedByName: profileDetail?.name || "",
    };
    sections.forEach((s) => {
      payload[s.beStart] = dateOnlyISO(values?.[s.startKey]);
      payload[s.beEnd] = dateOnlyISO(values?.[s.endKey]);
    });
    updateData(payload);
  };

  if (loading && !values) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box component="form" id={props.id} onSubmit={handleSubmit} padding="16px 24px">
      <Box
        display="flex"
        alignItems="center"
        padding="8px 16px"
        bgcolor="#e3f2fd"
        border="solid 1px #90caf9"
        borderRadius={4}
      >
        <DateRange style={{ marginRight: 12, color: "#1976d2" }} />
        <Typography variant="body2">
          Đang cấu hình cho năm học{" "}
          <b>
            {props.year} – {props.year + 1}
          </b>
          . Đổi năm ở thanh phía trên trước khi mở Cấu hình.
        </Typography>
      </Box>

      {sections.map((s) => {
        const status = sectionStatus(s);
        return (
          <SectionCard
            key={s.startKey}
            icon={s.icon}
            title={
              <Box display="flex" alignItems="center" gridGap={12} width="100%">
                <span>{s.title}</span>
                <Chip
                  label={status.label}
                  size="small"
                  style={{
                    backgroundColor: status.bg,
                    color: "#fff",
                    fontWeight: 500,
                    height: 22,
                    fontSize: 12,
                  }}
                />
              </Box>
            }
          >
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <KPTextfield
                  id={s.startKey}
                  type="date"
                  label="Ngày bắt đầu"
                  value={values?.[s.startKey] || ""}
                  handleChangeForm={handleChange}
                />
              </Grid>
              <Grid item xs={6}>
                <KPTextfield
                  id={s.endKey}
                  type="date"
                  label="Ngày kết thúc"
                  value={values?.[s.endKey] || ""}
                  handleChangeForm={handleChange}
                />
              </Grid>
            </Grid>
          </SectionCard>
        );
      })}
    </Box>
  );
}

export default SettingForm;
